import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail } from '../services/users';
import LanguageSwitcher from '../components/languageSwitcher';
import './auth.css';

const VerifyEmailPage = () => {

    const { t } = useTranslation();
    const { token } = useParams()
    const navigate = useNavigate()

    // status: 'verifying' | 'success' | 'error'
    const [status, setStatus] = useState('verifying')
    const [message, setMessage] = useState('')

    useEffect(() => {
        if (!token) {
            setStatus('error')
            setMessage(t('auth.noVerificationToken'))
            return
        }

        let cancelled = false
        const run = async () => {
            try {
                const body = await verifyEmail(token)
                if (cancelled) return

                if (body && body.code === 200) {
                    setStatus('success')
                    setMessage(typeof body.message === 'string' ? body.message : t('auth.emailVerified'))
                    // Auto-redirect to /login after 3s so the user can act on it.
                    setTimeout(() => navigate('/login', { replace: true }), 3000)
                } else {
                    setStatus('error')
                    setMessage(typeof body?.message === 'string'
                        ? body.message
                        : t('auth.verifyFallbackError'))
                }
            } catch (e) {
                if (cancelled) return
                setStatus('error')
                setMessage(t('auth.verifyError'))
            }
        }
        run()
        return () => { cancelled = true }
    }, [token, navigate])

    return (
        <div className="auth-page">
            <div className="auth-shell">

                {/* ───────── LEFT / BRAND PANEL ───────── */}
                <aside className="auth-brand-panel">
                    <div className="auth-brand-top">
                        <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                            <img
                                src={process.env.PUBLIC_URL + '/logo-soroby.svg'}
                                alt="Soroby"
                                className="auth-brand-logo"
                            />
                        </Link>
                    </div>

                    <div className="auth-brand-message">
                        <h1 className="auth-brand-title" dangerouslySetInnerHTML={{ __html: t('auth.brandTitle') }} />
                        <p className="auth-brand-tagline">
                            {t('auth.brandTagline')}
                        </p>
                    </div>

                    <div className="auth-brand-footer">
                        <span>© {new Date().getFullYear()} Soroby · {t('auth.builtForServiceBusinesses')}</span>
                        <span>soroby.com</span>
                    </div>
                </aside>

                {/* ───────── RIGHT / STATUS PANEL ───────── */}
                <main className="auth-form-panel">
                    <LanguageSwitcher className="auth-lang" id="auth-language-drop" drop="down" align="end" showIcon />

                    <div className="auth-form-inner">

                        <div className="auth-mobile-brand">
                            <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                                <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                            </Link>
                        </div>

                        {status === 'verifying' && (
                            <>
                                <div className="auth-success-icon auth-success-icon--neutral" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="auth-spin">
                                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                    </svg>
                                </div>
                                <div className="auth-form-heading">
                                    <h2>{t('auth.verifyingEmail')}</h2>
                                    <p>{t('auth.hangTight')}</p>
                                </div>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <div className="auth-success-icon auth-success-icon--ok" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 6L9 17l-5-5" />
                                    </svg>
                                </div>
                                <div className="auth-form-heading">
                                    <h2>{t('auth.youAreVerified')}</h2>
                                    <p>{message} {t('auth.canNowSignIn')}</p>
                                </div>
                                <Link to="/login" className="auth-btn">
                                    <span>{t('auth.goToSignIn')}</span>
                                    <span className="arrow" aria-hidden="true">→</span>
                                </Link>
                                <p className="auth-secondary">{t('auth.redirecting')}</p>
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <div className="auth-success-icon auth-success-icon--error" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M12 8v4" />
                                        <path d="M12 16h.01" />
                                    </svg>
                                </div>
                                <div className="auth-form-heading">
                                    <h2>{t('auth.linkDidntWork')}</h2>
                                    <p>{message}</p>
                                </div>
                                <Link to="/register" className="auth-btn">
                                    <span>{t('auth.backToSignUp')}</span>
                                    <span className="arrow" aria-hidden="true">→</span>
                                </Link>
                                <p className="auth-secondary">
                                    {t('auth.alreadyHaveAccountShort')} <Link to="/login">{t('auth.signIn')}</Link>.
                                </p>
                            </>
                        )}

                    </div>
                </main>

            </div>
        </div>
    )
}

export default VerifyEmailPage;
